import { useState, useEffect, useMemo } from 'react';
import { apiClient } from '../lib/api-client';
import { langName } from '../lib/lang-names';

interface RunRecord {
  doc_key: string;
  status: string;
  cost_usd: number;
  language_code: string;
  level: string;
  run_type?: string;
  created_at: string;
  completed_at?: string;
  current_step: number;
  total_steps: number;
  source_base_lang?: string;
  target_base_lang?: string;
}

interface MonthBucket {
  month: string;
  fixqa: number;
  loc: number;
  total: number;
  runs: number;
}

interface LangCost {
  code: string;
  fixqa: number;
  loc: number;
  total: number;
  runs: number;
}

const MONTHLY_CAP = 1000;

function formatUsd(n: number): string {
  return n < 0.01 && n > 0 ? `$${n.toFixed(4)}` : `$${n.toFixed(2)}`;
}

function monthKey(iso: string): string {
  return iso.slice(0, 7);
}

export default function Financials() {
  const [fixqaRuns, setFixqaRuns] = useState<RunRecord[]>([]);
  const [locRuns, setLocRuns] = useState<RunRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([
      apiClient<{ runs: RunRecord[] }>('/api/admin/docs/fixqa/history').catch(() => ({ runs: [] })),
      apiClient<{ runs: RunRecord[] }>('/api/admin/docs/localize/history').catch(() => ({ runs: [] })),
    ])
      .then(([fq, loc]) => {
        setFixqaRuns(fq.runs);
        setLocRuns(loc.runs);
      })
      .catch(err => setError(err instanceof Error ? err.message : 'Failed to load'))
      .finally(() => setLoading(false));
  }, []);

  const allRuns = useMemo(() => [
    ...fixqaRuns.map(r => ({ ...r, _type: 'fixqa' as const })),
    ...locRuns.map(r => ({ ...r, _type: 'loc' as const })),
  ], [fixqaRuns, locRuns]);

  const totalSpend = useMemo(() => allRuns.reduce((s, r) => s + (r.cost_usd || 0), 0), [allRuns]);
  const completedRuns = useMemo(() => allRuns.filter(r => r.status === 'completed'), [allRuns]);
  const failedRuns = useMemo(() => allRuns.filter(r => r.status === 'failed'), [allRuns]);

  const byMonth = useMemo(() => {
    const map = new Map<string, MonthBucket>();
    for (const r of allRuns) {
      const m = monthKey(r.created_at || '');
      if (!m) continue;
      const b = map.get(m) || { month: m, fixqa: 0, loc: 0, total: 0, runs: 0 };
      const cost = r.cost_usd || 0;
      if (r._type === 'loc') b.loc += cost; else b.fixqa += cost;
      b.total += cost;
      b.runs++;
      map.set(m, b);
    }
    return [...map.values()].sort((a, b) => b.month.localeCompare(a.month));
  }, [allRuns]);

  const byLang = useMemo(() => {
    const map = new Map<string, LangCost>();
    for (const r of allRuns) {
      const code = r.language_code || 'unknown';
      const lc = map.get(code) || { code, fixqa: 0, loc: 0, total: 0, runs: 0 };
      const cost = r.cost_usd || 0;
      if (r._type === 'loc') lc.loc += cost; else lc.fixqa += cost;
      lc.total += cost;
      lc.runs++;
      map.set(code, lc);
    }
    return [...map.values()].sort((a, b) => b.total - a.total);
  }, [allRuns]);

  const currentMonth = new Date().toISOString().slice(0, 7);
  const currentMonthSpend = byMonth.find(b => b.month === currentMonth)?.total || 0;
  const capPct = MONTHLY_CAP > 0 ? (currentMonthSpend / MONTHLY_CAP) * 100 : 0;
  const capColor = capPct >= 90 ? 'text-red-400' : capPct >= 60 ? 'text-amber-400' : 'text-emerald-400';
  const barColor = capPct >= 90 ? 'bg-red-500' : capPct >= 60 ? 'bg-amber-500' : 'bg-teal-500';

  const recentRuns = useMemo(
    () => [...allRuns].sort((a, b) => (b.created_at || '').localeCompare(a.created_at || '')).slice(0, 30),
    [allRuns],
  );

  if (loading) {
    return <div className="flex items-center justify-center min-h-[40vh]"><p className="text-zinc-500 font-mono text-sm">Loading financial data...</p></div>;
  }

  if (error) {
    return <div className="flex items-center justify-center min-h-[40vh]"><p className="text-red-400 font-mono text-sm">{error}</p></div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl md:text-2xl font-bold text-zinc-100">Financials</h1>
        <p className="text-zinc-400 text-sm mt-1">API spend tracking across FIX_QA and Localization pipelines</p>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-zinc-800 border border-zinc-700 rounded-lg p-3 md:p-4">
          <div className="text-zinc-500 text-xs uppercase tracking-wider">Total Spend</div>
          <div className="text-xl md:text-2xl font-bold text-zinc-100 mt-1">{formatUsd(totalSpend)}</div>
        </div>
        <div className="bg-zinc-800 border border-zinc-700 rounded-lg p-3 md:p-4">
          <div className="text-zinc-500 text-xs uppercase tracking-wider">{currentMonth} Spend</div>
          <div className={`text-xl md:text-2xl font-bold mt-1 ${capColor}`}>{formatUsd(currentMonthSpend)}</div>
        </div>
        <div className="bg-zinc-800 border border-zinc-700 rounded-lg p-3 md:p-4">
          <div className="text-zinc-500 text-xs uppercase tracking-wider">Completed</div>
          <div className="text-xl md:text-2xl font-bold text-emerald-400 mt-1">{completedRuns.length}</div>
        </div>
        <div className="bg-zinc-800 border border-zinc-700 rounded-lg p-3 md:p-4">
          <div className="text-zinc-500 text-xs uppercase tracking-wider">Failed</div>
          <div className="text-xl md:text-2xl font-bold text-red-400 mt-1">{failedRuns.length}</div>
        </div>
      </div>

      {/* Monthly cap bar */}
      <div className="bg-zinc-800 border border-zinc-700 rounded-lg p-3 md:p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-zinc-400 text-sm">Monthly Budget ({currentMonth})</span>
          <span className="text-zinc-300 text-sm font-mono">{formatUsd(currentMonthSpend)} / {formatUsd(MONTHLY_CAP)}</span>
        </div>
        <div className="w-full bg-zinc-700 rounded-full h-3">
          <div className={`${barColor} h-3 rounded-full transition-all`} style={{ width: `${Math.min(capPct, 100)}%` }} />
        </div>
        <div className="flex justify-between mt-1">
          <span className={`text-xs font-mono ${capColor}`}>{capPct.toFixed(1)}%</span>
          <span className="text-xs text-zinc-600">cap: ${MONTHLY_CAP}</span>
        </div>
      </div>

      {/* Monthly breakdown + Per-language breakdown side by side */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Monthly breakdown */}
        <div className="bg-zinc-800 border border-zinc-700 rounded-lg p-3 md:p-4">
          <h2 className="text-zinc-300 font-medium mb-3">Monthly Breakdown</h2>
          {byMonth.length === 0 ? (
            <p className="text-zinc-600 text-sm">No cost data yet.</p>
          ) : (
            <div className="space-y-2">
              {byMonth.map(b => (
                <div key={b.month} className="flex items-center justify-between text-sm">
                  <span className="text-zinc-400 font-mono">{b.month}</span>
                  <div className="flex items-center gap-4">
                    <span className="text-teal-400 text-xs">{b.runs} runs</span>
                    {b.fixqa > 0 && <span className="text-xs text-zinc-500">FIX_QA: {formatUsd(b.fixqa)}</span>}
                    {b.loc > 0 && <span className="text-xs text-purple-400">Loc: {formatUsd(b.loc)}</span>}
                    <span className="text-zinc-200 font-mono font-medium">{formatUsd(b.total)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Per-language cost */}
        <div className="bg-zinc-800 border border-zinc-700 rounded-lg p-3 md:p-4">
          <h2 className="text-zinc-300 font-medium mb-3">Spend by Language</h2>
          {byLang.length === 0 ? (
            <p className="text-zinc-600 text-sm">No cost data yet.</p>
          ) : (
            <div className="space-y-1 max-h-64 overflow-y-auto">
              {byLang.map(lc => (
                <div key={lc.code} className="flex items-center justify-between text-sm py-0.5">
                  <div className="flex items-center gap-2">
                    <span className="text-zinc-300 font-mono text-xs w-16" title={langName(lc.code)}>{lc.code}</span>
                    <span className="text-zinc-600 text-xs">{lc.runs} runs</span>
                  </div>
                  <div className="flex items-center gap-3">
                    {lc.fixqa > 0 && <span className="text-xs text-zinc-500">{formatUsd(lc.fixqa)}</span>}
                    {lc.loc > 0 && <span className="text-xs text-purple-400">{formatUsd(lc.loc)}</span>}
                    <span className="text-zinc-200 font-mono text-xs font-medium w-16 text-right">{formatUsd(lc.total)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Recent runs table */}
      <div className="bg-zinc-800 border border-zinc-700 rounded-lg p-3 md:p-4">
        <h2 className="text-zinc-300 font-medium mb-3">Recent Runs (last 30)</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[580px]">
            <thead>
              <tr className="text-zinc-500 text-xs text-left border-b border-zinc-700">
                <th className="pb-2 font-medium">Type</th>
                <th className="pb-2 font-medium">Language</th>
                <th className="pb-2 font-medium">Level</th>
                <th className="pb-2 font-medium">Status</th>
                <th className="pb-2 font-medium">Steps</th>
                <th className="pb-2 font-medium text-right">Cost</th>
                <th className="pb-2 font-medium text-right">Date</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-700/50">
              {recentRuns.map((r, i) => {
                const statusColor =
                  r.status === 'completed' ? 'text-emerald-400' :
                  r.status === 'failed' ? 'text-red-400' :
                  r.status === 'running' ? 'text-teal-400' :
                  r.status === 'cancelled' ? 'text-zinc-500' : 'text-amber-400';
                return (
                  <tr key={r.doc_key + r.created_at + i} className="hover:bg-zinc-700/20">
                    <td className="py-1.5">
                      <span className={`text-xs font-mono px-1.5 py-0.5 rounded ${
                        r._type === 'loc' ? 'bg-purple-900/30 text-purple-300' :
                        r.run_type === 'fix' ? 'bg-amber-900/30 text-amber-300' :
                        'bg-teal-900/30 text-teal-300'
                      }`}>
                        {r._type === 'loc' ? 'LOC' : r.run_type === 'fix' ? 'FIX' : 'FIX_QA'}
                      </span>
                    </td>
                    <td className="py-1.5 font-mono text-zinc-300 text-xs" title={langName(r.language_code)}>{r.language_code}</td>
                    <td className="py-1.5 font-mono text-zinc-400 text-xs">{r.level}</td>
                    <td className={`py-1.5 text-xs ${statusColor}`}>{r.status}</td>
                    <td className="py-1.5 text-zinc-500 text-xs">{(r.current_step || 0) + 1}/{r.total_steps || '?'}</td>
                    <td className="py-1.5 text-right font-mono text-zinc-200 text-xs">{formatUsd(r.cost_usd || 0)}</td>
                    <td className="py-1.5 text-right text-zinc-600 text-xs">{(r.created_at || '').slice(0, 16)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {recentRuns.length === 0 && <p className="text-zinc-600 text-sm text-center py-4">No runs recorded yet.</p>}
      </div>
    </div>
  );
}
